<script setup lang="ts">
// P6 账户安全面板：改 PIN（apply_cloud_wrap 路线）/ 恢复码重新生成 / 设备管理 / 审计查看。
// 改 PIN 与恢复码回写经 vault-session（VDK 不变、字段密文不动）；设备/审计走 keytail-server 只读端点。
import { onMounted, reactive, ref } from 'vue'
import { session, changePin, regenerateRecoveryCode } from '../vault-session'
import * as api from '../api'
import type { AuditEntry } from '../lib/model'

const emit = defineEmits<{ (e: 'close'): void }>()

type Pane = 'security' | 'devices' | 'audit'
const pane = ref<Pane>('security')

// ── 改 PIN ──
const pinForm = reactive({ old: '', neo: '', neo2: '' })
const pinErr = ref('')
const pinOk = ref('')
async function onChangePin() {
  pinErr.value = ''
  pinOk.value = ''
  if (pinForm.neo.length < 8) return (pinErr.value = '新主密码至少 8 位')
  if (pinForm.neo !== pinForm.neo2) return (pinErr.value = '两次新主密码不一致')
  try {
    await changePin(pinForm.old, pinForm.neo)
    pinForm.old = pinForm.neo = pinForm.neo2 = ''
    pinOk.value = '主密码已更新。'
  } catch (e) {
    pinErr.value = (e as Error).message || '改主密码失败'
  }
}

// ── 恢复码 ──
const recoveryErr = ref('')
async function onRegenRecovery() {
  recoveryErr.value = ''
  if (!confirm('重新生成会作废旧恢复码，新码只展示一次。确定继续？')) return
  try {
    await regenerateRecoveryCode() // 新码经 session.lastRecoveryCode 弹窗展示
  } catch (e) {
    recoveryErr.value = (e as Error).message || '生成恢复码失败'
  }
}

// ── 设备 ──
const devices = ref<api.DeviceView[]>([])
const devLoading = ref(false)
const devErr = ref('')
async function loadDevices() {
  devLoading.value = true
  devErr.value = ''
  try {
    devices.value = await api.listDevices()
  } catch (e) {
    devErr.value = (e as Error).message || '加载设备失败'
  } finally {
    devLoading.value = false
  }
}
async function onRevoke(d: api.DeviceView) {
  if (!confirm(`撤销设备「${d.label || d.id}」？该设备将无法再获取解锁协助。`)) return
  try {
    await api.revokeDevice(d.id)
    await loadDevices()
  } catch (e) {
    devErr.value = (e as Error).message || '撤销失败'
  }
}

// ── 审计 ──
const cloudAudit = ref<api.AuditView[]>([])
const auditLoading = ref(false)
const auditErr = ref('')
const localAudit = ref<AuditEntry[]>([])
async function loadAudit() {
  localAudit.value = ((session.payload.audit ?? []) as unknown as AuditEntry[]).slice().reverse()
  auditLoading.value = true
  auditErr.value = ''
  try {
    cloudAudit.value = await api.listAudit(50)
  } catch (e) {
    auditErr.value = (e as Error).message || '加载审计失败'
  } finally {
    auditLoading.value = false
  }
}

function go(p: Pane) {
  pane.value = p
  if (p === 'devices' && !devices.value.length) loadDevices()
  if (p === 'audit') loadAudit()
}

function fmt(t?: string): string {
  if (!t) return '—'
  try {
    return new Date(t).toLocaleString()
  } catch {
    return t
  }
}

onMounted(() => {})
</script>

<template>
  <div class="kt-modal" role="dialog" aria-modal="true" @keydown.esc="emit('close')">
    <div class="kt-modal__panel kt-modal__panel--wide">
      <header class="kt-modal__head">
        <h2>账户安全</h2>
        <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="emit('close')" aria-label="关闭">✕</button>
      </header>

      <nav class="kt-subtabs">
        <button :class="{ on: pane === 'security' }" @click="go('security')">主密码 / 恢复码</button>
        <button :class="{ on: pane === 'devices' }" @click="go('devices')">设备</button>
        <button :class="{ on: pane === 'audit' }" @click="go('audit')">审计</button>
      </nav>

      <!-- 安全：改 PIN + 恢复码 -->
      <div v-if="pane === 'security'" class="kt-form">
        <h3 class="kt-sec-title">修改主密码</h3>
        <p class="kt-hint">改主密码不改你的密钥本身——只用新主密码重新包装本地密钥（VDK 不变），一次回写云端。改完请用新主密码解锁。</p>
        <label class="kt-label">当前主密码</label>
        <input class="kt-input" v-model="pinForm.old" type="password" autocomplete="current-password" :disabled="session.busy" />
        <label class="kt-label">新主密码（至少 8 位）</label>
        <input class="kt-input" v-model="pinForm.neo" type="password" autocomplete="new-password" :disabled="session.busy" />
        <label class="kt-label">确认新主密码</label>
        <input class="kt-input" v-model="pinForm.neo2" type="password" autocomplete="new-password" :disabled="session.busy" @keydown.enter="onChangePin" />
        <p v-if="pinErr" class="kt-error" role="alert">{{ pinErr }}</p>
        <p v-else-if="pinOk" class="kt-notice">{{ pinOk }}</p>
        <p v-if="session.busy && session.busyStep" class="kt-progress"><span class="kt-dot" /> {{ session.busyStep }}</p>
        <button class="kt-btn kt-btn--primary" :disabled="session.busy" @click="onChangePin">更新主密码</button>

        <hr class="kt-sep" />
        <h3 class="kt-sec-title">恢复码</h3>
        <p class="kt-hint">
          恢复码用于忘记主密码时找回。我们只保存它的指纹、不保存明文，所以无法再次显示旧码——只能重新生成一张新码。
          注意：网页端生成的恢复码暂不支持短信自助重置，短信重置仍需在桌面端补登记一次。
        </p>
        <p v-if="recoveryErr" class="kt-error" role="alert">{{ recoveryErr }}</p>
        <button class="kt-btn kt-btn--ghost" :disabled="session.busy" @click="onRegenRecovery">重新生成恢复码</button>
      </div>

      <!-- 设备 -->
      <div v-else-if="pane === 'devices'">
        <p class="kt-hint">这些设备可获取解锁协助（S_server 分片）。撤销陌生或丢失的设备以收紧访问。</p>
        <p v-if="devErr" class="kt-error" role="alert">{{ devErr }}</p>
        <p v-if="devLoading" class="kt-progress"><span class="kt-dot" /> 加载中…</p>
        <ul class="kt-list">
          <li v-for="d in devices" :key="d.id" class="kt-item" :class="{ 'kt-item--off': !!d.revokedAt }">
            <div class="kt-item__main">
              <div class="kt-item__title">
                {{ d.label || '(未命名设备)' }}
                <span class="kt-tagchip">{{ d.kind }}</span>
                <span v-if="d.revokedAt" class="kt-tagchip kt-tagchip--off">已撤销</span>
              </div>
              <div class="kt-item__sub">登记于 {{ fmt(d.enrolledAt) }}<span v-if="d.lastSeenAt"> · 最近活跃 {{ fmt(d.lastSeenAt) }}</span></div>
            </div>
            <div class="kt-item__actions">
              <button v-if="!d.revokedAt" class="kt-btn kt-btn--ghost kt-btn--sm kt-del" @click="onRevoke(d)">撤销</button>
            </div>
          </li>
          <li v-if="!devLoading && !devices.length" class="kt-empty">还没有登记的设备。</li>
        </ul>
      </div>

      <!-- 审计 -->
      <div v-else>
        <p class="kt-hint">云端记录解锁/上传等服务端可见的访问事件；本地记录这台设备上的操作。均为只读。</p>
        <p v-if="auditErr" class="kt-error" role="alert">{{ auditErr }}</p>
        <p v-if="auditLoading" class="kt-progress"><span class="kt-dot" /> 加载中…</p>
        <h3 class="kt-sec-title">云端访问</h3>
        <ul class="kt-list">
          <li v-for="(e, i) in cloudAudit" :key="'c' + i" class="kt-item">
            <div class="kt-item__main">
              <div class="kt-item__title">{{ e.action }}</div>
              <div class="kt-item__sub">{{ fmt(e.createdAt) }}<span v-if="e.detail"> · {{ e.detail }}</span><span v-if="e.deviceId"> · {{ e.deviceId }}</span></div>
            </div>
          </li>
          <li v-if="!auditLoading && !cloudAudit.length" class="kt-empty">暂无云端访问记录。</li>
        </ul>
        <h3 class="kt-sec-title">本设备操作</h3>
        <ul class="kt-list">
          <li v-for="e in localAudit" :key="e.id" class="kt-item">
            <div class="kt-item__main">
              <div class="kt-item__title">{{ e.action }}</div>
              <div class="kt-item__sub">{{ fmt(e.created_at) }}<span v-if="e.detail"> · {{ e.detail }}</span></div>
            </div>
          </li>
          <li v-if="!localAudit.length" class="kt-empty">本设备暂无操作记录。</li>
        </ul>
      </div>

      <div class="kt-modal__foot">
        <button class="kt-btn kt-btn--ghost" @click="emit('close')">关闭</button>
      </div>
    </div>
  </div>
</template>
